import { ApolloError, useApolloClient } from '@apollo/client';
import { useEffect, useMemo, useState } from 'react';
import {
  ClientType,
  DiscountUsage,
  GetDiscountsPaginatedDocument,
  GetDiscountsPaginatedQuery,
  PackageType,
  PartnershipType,
  ScopeType,
  useGetAllClientsQuery,
  useGetAllPackagesQuery,
  useGetDiscountsPaginatedQuery,
  useGetDiscountUsageLazyQuery,
  useGetPartnershipsQuery,
} from '../types';
import { computeActiveStatus } from '../utils/contract-utils';
import { DiscountType, IDiscount } from '../utils/discount-utils';
import { fetchAllPages } from '../utils/fetch-all-pages';
import { cleanTypeNameGraphql } from '../utils/graphql-utils';

type PaginatedDiscountItem =
  GetDiscountsPaginatedQuery['getDiscountsPaginated']['items'][number];

const mapPaginatedDiscounts = (
  items: PaginatedDiscountItem[],
  discountUsage: {
    data?: DiscountUsage[];
    loading: boolean;
    error?: ApolloError;
  },
): IDiscount[] =>
  // ACCOUNTS-AND-PLAN-MIGRATION: item.clientId, item.insurerId, item.partnershipId are client/insurer/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  items.map(item => ({
    clientId: item.clientId,
    discountDefinitionId: item.id,
    discountType: item.discountType,
    endDate: item.endDate,
    insurerId: item.insurerId,
    name: item.discountDetails[0]?.name ?? null,
    partnershipId: item.partnershipId,
    scopes:
      item.scopes?.map(s => ({
        scopeableType: s.scopeableType,
        scopeableId: s.scopeableId ?? null,
      })) ?? [],
    startDate: item.startDate,
    suppressEmptyCharges: item.suppressEmptyCharges,
    status: computeActiveStatus(false, item.startDate, item.endDate),
    discountDetails: item.discountDetails
      .map(detail => ({
        discount: detail.discount,
        discountDetailId: detail.id,
        milestoneDiscount: detail.milestoneDiscount ?? {},
        subscriptionTier: detail.subscriptionTier,
        tier: detail.tier,
        discountUsage: {
          ...discountUsage.data?.find(d => d.discountDetailId === detail.id),
          loading: discountUsage.loading,
          error: discountUsage.error,
        },
      }))
      .sort((a, b) => a.tier - b.tier),
  }));

const ALL_DISCOUNTS_PAGE_SIZE = 25;

export interface GetAllDiscountsPaginatedOptions {
  clientId?: number;
  partnershipIds?: number[];
  discountType?: string;
  inquiryDate?: string;
  skip?: boolean;
}

export interface UseGetAllDiscountsPaginatedResult {
  data: IDiscount[];
  isLoading: boolean;
  hasError: boolean;
  error?: ApolloError;
  refetch: () => void;
}

export const useGetAllDiscountsPaginated = ({
  clientId,
  partnershipIds,
  discountType,
  inquiryDate,
  skip,
}: GetAllDiscountsPaginatedOptions): UseGetAllDiscountsPaginatedResult => {
  const client = useApolloClient();
  const [data, setData] = useState<IDiscount[]>([]);
  const [isLoading, setIsLoading] = useState(!skip);
  const [error, setError] = useState<ApolloError>();
  const [refetchCount, setRefetchCount] = useState(0);

  const refetch = (): void => setRefetchCount(c => c + 1);

  useEffect(() => {
    if (skip) {
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();

    const fetchPage = async (
      page: number,
      pageSize: number,
    ): Promise<
      { items: PaginatedDiscountItem[]; total: number } | undefined
    > => {
      // ACCOUNTS-AND-PLAN-MIGRATION: clientId/partnershipIds are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
      const result = await client.query<GetDiscountsPaginatedQuery>({
        query: GetDiscountsPaginatedDocument,
        variables: {
          clientId,
          partnershipIds,
          discountType,
          inquiryDate,
          scopeType: ScopeType.Inclusive,
          page,
          size: pageSize,
        },
        fetchPolicy: 'network-only',
        context: { fetchOptions: { signal: controller.signal } },
      });

      const paginated = result.data.getDiscountsPaginated;

      return { items: paginated.items, total: paginated.total ?? 0 };
    };

    const fetchData = async (): Promise<void> => {
      setIsLoading(true);
      setError(undefined);

      try {
        const allItems = await fetchAllPages(fetchPage, {
          pageSize: ALL_DISCOUNTS_PAGE_SIZE,
          signal: controller.signal,
        });

        if (!controller.signal.aborted) {
          setData(mapPaginatedDiscounts(allItems, { loading: false }));
        }
      } catch (e) {
        if (!controller.signal.aborted) {
          setError(e as ApolloError);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return (): void => {
      controller.abort();
    };
  }, [
    client,
    clientId,
    partnershipIds,
    discountType,
    inquiryDate,
    skip,
    refetchCount,
  ]);

  return { data, isLoading, hasError: !!error, error, refetch };
};

export interface GetDiscountsPaginatedOptions {
  clientId: number;
  partnershipIds?: number[];
  page: number;
  size: number;
  skip?: boolean;
}

export interface UseGetDiscountsPaginatedResult {
  data: IDiscount[];
  isLoading: boolean;
  hasError: boolean;
  error?: ApolloError;
  total: number;
  usageLoading: boolean;
  usageError?: ApolloError;
}

export const useGetDiscountsPaginated = ({
  clientId,
  partnershipIds,
  page,
  size,
  skip,
}: GetDiscountsPaginatedOptions): UseGetDiscountsPaginatedResult => {
  // ACCOUNTS-AND-PLAN-MIGRATION: clientId/partnershipIds are client/partnership-scoped, not resolvable via contract_id — will be addressed by the Accounts & Plan migration.
  const {
    data: paginatedData,
    loading,
    error,
  } = useGetDiscountsPaginatedQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      clientId,
      partnershipIds,
      scopeType: ScopeType.Inclusive,
      page,
      size,
    },
    skip,
  });

  const items = useMemo(
    () => paginatedData?.getDiscountsPaginated?.items ?? [],
    [paginatedData],
  );
  const total = paginatedData?.getDiscountsPaginated?.total ?? 0;

  const [
    getDiscountUsage,
    { data: usageData, loading: usageLoading, error: usageError },
  ] = useGetDiscountUsageLazyQuery();

  useEffect(() => {
    if (items.length) {
      const detailIds = items.flatMap(item =>
        item.discountDetails.map(d => d.id),
      );

      getDiscountUsage({
        variables: { clientId, discountDetailIds: detailIds },
      });
    }
  }, [items, clientId, getDiscountUsage]);

  const data = useMemo(
    () =>
      items.length
        ? mapPaginatedDiscounts(items, {
            data: usageData?.getDiscountUsage,
            loading: usageLoading,
            error: usageError,
          })
        : [],
    [items, usageData, usageLoading, usageError],
  );

  return {
    data,
    isLoading: loading,
    hasError: !!error || !!usageError,
    error,
    total,
    usageLoading,
    usageError,
  };
};

export interface UseVbdPageDataResult {
  vbds: IDiscount[];
  vbdsLoading: boolean;
  refetchVbds: () => void;
  partnerships: PartnershipType[];
  partnershipsLoading: boolean;
  clients: ClientType[];
  clientsLoading: boolean;
  packages: PackageType[];
  packagesLoading: boolean;
  partnershipMap: Map<number, string>;
  clientMap: Map<number, string>;
}
// useGetAllDiscountsPaginated is used instead of useGetDiscountsPaginated
// because the paginated variant fires a secondary getDiscountUsage query that
// requires a clientId. The global VBD view has no single clientId (it shows
// all VBDs across all clients/partnerships), and usage data is not needed here.
export const useVbdPageData = (): UseVbdPageDataResult => {
  const {
    data: vbds,
    isLoading: vbdsLoading,
    refetch: refetchVbds,
  } = useGetAllDiscountsPaginated({
    discountType: DiscountType.volumeBasedDiscounts,
  });

  const { data: partnershipsData, loading: partnershipsLoading } =
    useGetPartnershipsQuery({ variables: { type: 'billing' } });
  const { data: clientsData, loading: clientsLoading } =
    useGetAllClientsQuery();
  const { data: packagesData, loading: packagesLoading } =
    useGetAllPackagesQuery();

  const { partnerships, partnershipMap } = useMemo(() => {
    const sorted = [...(partnershipsData?.getPartnerships ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const map = new Map<number, string>();
    sorted.forEach(p => map.set(p.id, p.name));
    return { partnerships: sorted, partnershipMap: map };
  }, [partnershipsData]);

  const { clients, clientMap } = useMemo(() => {
    const sorted = [...(clientsData?.getAllClients ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const map = new Map<number, string>();
    sorted.forEach(c => map.set(c.id, c.name));
    return { clients: sorted, clientMap: map };
  }, [clientsData]);

  const packages = useMemo(
    () =>
      [
        ...(packagesData
          ? cleanTypeNameGraphql(packagesData.getAllPackages)
          : []),
      ].sort((a, b) => a.name.localeCompare(b.name)),
    [packagesData],
  );

  return {
    vbds,
    vbdsLoading,
    refetchVbds,
    partnerships,
    partnershipsLoading,
    clients,
    clientsLoading,
    packages,
    packagesLoading,
    partnershipMap,
    clientMap,
  };
};
